import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  Order,
  OrderDetails,
  CreateOrderDto,
  UpdateOrderDto,
  OrderFilters,
  OrdersData,
  AssignDriverResult,
  FilterPreset,
} from '../models/order.model';
import { CommissionRecalculationLog, CommissionRecalculationResult } from '../models/commission-recalculation.model';
import { ApiResponse } from '../interfaces';

/**
 * OrdersService
 * 
 * Complete service for order management in dispatcher panel
 * Based on: docs_frontend/order-flow/final-review/04_API_REQUIREMENTS.md
 */
@Injectable({
  providedIn: 'root'
})
export class OrdersService {
  private readonly apiUrl = `${environment.apiUrl}/company/orders`;

  constructor(private http: HttpClient) {}

  /**
   * Get orders list with filters and pagination
   * 
   * @param filters - Order filters
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 50)
   */
  getOrders(
    filters?: OrderFilters,
    page: number = 1,
    limit: number = 50
  ): Observable<ApiResponse<OrdersData>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (filters) {
      // Status filter (can be single or multiple)
      if (filters.status) {
        const statuses = Array.isArray(filters.status) 
          ? filters.status.join(',') 
          : filters.status.toString();
        params = params.set('status', statuses);
      }

      // Driver filter
      if (filters.driverId) {
        params = params.set('driverId', filters.driverId.toString());
      }

      // Client filter
      if (filters.clientId) {
        params = params.set('clientId', filters.clientId.toString());
      }

      // Date range
      if (filters.dateFrom) {
        params = params.set('dateFrom', filters.dateFrom.toISOString());
      }
      if (filters.dateTo) {
        params = params.set('dateTo', filters.dateTo.toISOString());
      }

      // Search query
      if (filters.search) {
        params = params.set('search', filters.search);
      }

      // Priority
      if (filters.priority) {
        if (Array.isArray(filters.priority)) {
          params = params.set('priority', filters.priority.join(','));
        } else {
          params = params.set('priority', filters.priority.toString());
        }
      }

      // Payment status
      if (filters.isPaid !== undefined) {
        params = params.set('isPaid', filters.isPaid.toString());
      }
    }

    return this.http.get<ApiResponse<OrdersData>>(this.apiUrl, { params });
  }

  /**
   * Get single order details with full information
   * 
   * @param orderId - Order ID
   */
  getOrderDetails(orderId: number): Observable<ApiResponse<OrderDetails>> {
    return this.http.get<ApiResponse<OrderDetails>>(`${this.apiUrl}/${orderId}`);
  }

  /**
   * Create new order (dispatcher creates for client)
   * 
   * @param data - Order creation data
   */
  createOrder(data: CreateOrderDto): Observable<ApiResponse<Order>> {
    return this.http.post<ApiResponse<Order>>(this.apiUrl, data);
  }

  /**
   * Update existing order
   * 
   * @param orderId - Order ID
   * @param data - Order update data
   */
  updateOrder(orderId: number, data: UpdateOrderDto): Observable<ApiResponse<Order>> {
    return this.http.put<ApiResponse<Order>>(`${this.apiUrl}/${orderId}`, data);
  }

  /**
   * Delete order (only DRAFT status)
   * 
   * @param orderId - Order ID
   */
  deleteOrder(orderId: number): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${orderId}`);
  }

  /**
   * Assign driver to order
   * 
   * @param orderId - Order ID
   * @param driverId - Driver ID
   * @param autoSearch - Was it auto or manual assignment
   * @param escalationLevel - Search level (1-3) if auto
   */
  assignDriver(
    orderId: number,
    driverId: number,
    autoSearch: boolean = false,
    escalationLevel?: number
  ): Observable<ApiResponse<AssignDriverResult>> {
    return this.http.post<ApiResponse<AssignDriverResult>>(
      `${this.apiUrl}/${orderId}/assign-driver`,
      { driverId, autoSearch, escalationLevel }
    );
  }

  /**
   * Get available drivers for order
   * 
   * @param orderId - Order ID
   * @param radius - Search radius in km (default: 5)
   * @param maxResults - Maximum drivers to return (default: 20)
   */
  getAvailableDrivers(
    orderId: number,
    radius: number = 5,
    maxResults: number = 20
  ): Observable<ApiResponse<any>> {
    const params = new HttpParams()
      .set('radius', radius.toString())
      .set('maxResults', maxResults.toString());

    return this.http.get<ApiResponse<any>>(
      `${this.apiUrl}/${orderId}/available-drivers`,
      { params }
    );
  }

  /**
   * Cancel driver assignment
   * 
   * @param orderId - Order ID
   * @param reason - Cancellation reason
   * @param notifyDriver - Send notification to driver
   */
  cancelAssignment(
    orderId: number,
    reason: string,
    notifyDriver: boolean = true
  ): Observable<ApiResponse<Order>> {
    return this.http.post<ApiResponse<Order>>(
      `${this.apiUrl}/${orderId}/cancel-assignment`,
      { reason, notifyDriver }
    );
  }

  /**
   * Change order status
   * 
   * @param orderId - Order ID
   * @param status - New status
   * @param reason - Reason for status change (optional)
   * @param notes - Additional notes (optional)
   */
  changeStatus(
    orderId: number,
    status: number,
    reason?: string,
    notes?: string
  ): Observable<ApiResponse<Order>> {
    return this.http.put<ApiResponse<Order>>(
      `${this.apiUrl}/${orderId}/status`,
      { status, reason, notes }
    );
  }

  /**
   * Cancel order
   * 
   * @param orderId - Order ID
   * @param reason - Cancellation reason
   * @param cancelledBy - Who cancelled (dispatcher, client, etc.)
   * @param notes - Additional notes
   */
  cancelOrder(
    orderId: number,
    reason: string,
    cancelledBy: string = 'dispatcher',
    notes?: string
  ): Observable<ApiResponse<Order>> {
    return this.http.post<ApiResponse<Order>>(
      `${this.apiUrl}/${orderId}/cancel`,
      { reason, cancelledBy, notes }
    );
  }

  /**
   * Mark order as paid
   * 
   * @param orderId - Order ID
   * @param payment - Payment information
   */
  markAsPaid(orderId: number, payment: any): Observable<ApiResponse<Order>> {
    return this.http.post<ApiResponse<Order>>(
      `${this.apiUrl}/${orderId}/mark-paid`,
      payment
    );
  }

  /**
   * Process refund
   * 
   * @param orderId - Order ID
   * @param amount - Refund amount
   * @param reason - Refund reason
   */
  processRefund(
    orderId: number,
    amount: number,
    reason: string
  ): Observable<ApiResponse<Order>> {
    return this.http.post<ApiResponse<Order>>(
      `${this.apiUrl}/${orderId}/refund`,
      { amount, reason }
    );
  }

  /**
   * Get order timeline (history of events)
   * 
   * @param orderId - Order ID
   */
  getTimeline(orderId: number): Observable<ApiResponse<any[]>> {
    return this.http.get<ApiResponse<any[]>>(`${this.apiUrl}/${orderId}/timeline`);
  }

  /**
   * Get GPS tracking data for order
   * 
   * @param orderId - Order ID
   */
  getGpsTracking(orderId: number): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(`${this.apiUrl}/${orderId}/gps-tracking`);
  }

  /**
   * Get trip breakdown (parts of trip for tariff calculation)
   * 
   * @param orderId - Order ID
   */
  getTripBreakdown(orderId: number): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(`${this.apiUrl}/${orderId}/trip-breakdown`);
  }

  /**
   * Export orders to file (Excel, CSV, PDF)
   * 
   * @param filters - Filter criteria
   * @param format - Export format
   */
  exportOrders(
    filters?: OrderFilters,
    format: 'excel' | 'csv' | 'pdf' = 'excel'
  ): Observable<Blob> {
    let params = new HttpParams().set('format', format);

    if (filters) {
      // Add same filters as getOrders
      if (filters.status) {
        const statuses = Array.isArray(filters.status) 
          ? filters.status.join(',') 
          : filters.status.toString();
        params = params.set('status', statuses);
      }
      // ... other filters
    }

    return this.http.get(`${this.apiUrl}/export`, {
      params,
      responseType: 'blob'
    });
  }

  /**
   * Get saved filter presets
   */
  getFilterPresets(): Observable<ApiResponse<FilterPreset[]>> {
    return this.http.get<ApiResponse<FilterPreset[]>>(`${this.apiUrl}/filter-presets`);
  }

  /**
   * Save filter preset
   * 
   * @param preset - Filter preset data
   */
  saveFilterPreset(preset: Omit<FilterPreset, 'id'>): Observable<ApiResponse<FilterPreset>> {
    return this.http.post<ApiResponse<FilterPreset>>(`${this.apiUrl}/filter-presets`, preset);
  }

  /**
   * Delete filter preset
   * 
   * @param presetId - Preset ID
   */
  deleteFilterPreset(presetId: number): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/filter-presets/${presetId}`);
  }

  /**
   * Get dispatcher statistics
   */
  getStatistics(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(`${environment.apiUrl}/dispatcher/orders/statistics`);
  }

  /**
   * Recalculate commission for a completed order against current settings.
   * Only updates commission sums - order date/time/status are untouched.
   * No-op (returns changed: false) if the recalculated split matches the
   * currently live one.
   */
  recalculateCommission(orderId: number): Observable<ApiResponse<CommissionRecalculationResult>> {
    return this.http.post<ApiResponse<CommissionRecalculationResult>>(
      `${this.apiUrl}/${orderId}/commission-recalculation`,
      {},
    );
  }

  /**
   * Get the commission recalculation audit trail for an order (newest first).
   */
  getCommissionRecalculationHistory(orderId: number): Observable<ApiResponse<CommissionRecalculationLog[]>> {
    return this.http.get<ApiResponse<CommissionRecalculationLog[]>>(
      `${this.apiUrl}/${orderId}/commission-recalculation-history`,
    );
  }

  /**
   * Roll back a commission recalculation log entry. Restores the wallets to
   * the state before that entry and removes it and every newer entry for
   * the order.
   */
  rollbackCommissionRecalculation(orderId: number, logId: number): Observable<ApiResponse<{ orderId: number }>> {
    return this.http.post<ApiResponse<{ orderId: number }>>(
      `${this.apiUrl}/${orderId}/commission-recalculation/${logId}/rollback`,
      {},
    );
  }
}
